import gql from 'graphql-tag';
import { GetServerSideProps, Redirect } from 'next';
import apolloClient from 'lib/apolloClient';
import { ContactForm } from 'components/tailgrids-ui/contact-form';
import { FrontasticRenderer, LocaleStorage, ResponseError, createClient, useAccount } from 'frontastic';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import nextI18nextConfig from 'next-i18next.config';
import { tastics } from 'frontastic/tastics';
import styles from '../slug.module.css';
import { useStrapiMenu } from 'helpers/hooks/useStrapiMenu';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FullPageWidthWrapper } from 'components/tailgrids-ui/full-bleed';
import { Fragment } from 'react';
import { ComponentMapper } from 'components/tailgrids-ui/strapi/ComponentMapper';

export default function Landing(props) {
  const router = useRouter();
  const strapiMenu = useStrapiMenu(props?.path[0], router.locale);
  const currentPath = `/${props?.path?.slice(1)?.join('/')}`;

  return (
    <div>
      <FrontasticRenderer data={props.data} tastics={tastics} wrapperClassName={styles.gridWrapper} isStrapiPage>
        <FullPageWidthWrapper>
          <div className="container mx-auto max-w-[1280px] px-4 pt-4 sm:px-10 md:pt-0 lg:px-4">
            <div className="mb-10 flex w-full flex-wrap gap-6 bg-[#102B33] py-4 px-6 text-sm md:text-base">
              {strapiMenu?.map((item, idx) => (
                <Fragment key={idx}>
                  {item?.items.length >= 0 &&
                    item.items.map((subItem, i) => (
                      <Link href={`/c${subItem.path}`} key={i}>
                        <span
                          className={`w-fit cursor-pointer text-base font-semibold hover:text-white ${
                            currentPath === `/${subItem.path.split('/')[2]}` ? 'text-[#33B8A1]' : 'text-white/70'
                          }`}
                        >
                          {subItem.title}
                        </span>
                      </Link>
                    ))}
                </Fragment>
              ))}
            </div>
          </div>

          <div>
            {props.content.attributes.body.map((component, idx) => {
              if (component.__typename === 'ComponentSectionsS55Form') {
                return (
                  <ContactForm
                    formName={component.formName}
                    submitText={component.submitText}
                    fields={component.fields}
                    id={component.id}
                    pageId={component.id}
                    key={`${component.__typename}-${component.id}`}
                  />
                );
              } else {
                return <ComponentMapper key={`${component.__typename}-${component.id}`} component={component} />;
              }
            })}
          </div>
        </FullPageWidthWrapper>
      </FrontasticRenderer>
    </div>
  );
}

export const getServerSideProps: GetServerSideProps | Redirect = async ({ params, locale, query, req, res }) => {
  LocaleStorage.locale = locale;

  const frontastic = createClient();
  const data = await frontastic.getRouteData(params, `{path: "/", locale: ${locale}}`, query, req, res);

  if (!params.path) {
    if (!query.currentPath || !query.currentLocale) {
      return {
        redirect: {
          destination: '/',
          statusCode: 302,
        },
      };
    }

    const currentPath = (query.currentPath as string).replace('/c', '');
    const currentLocale = (query.currentLocale as string).replace('_', '-');
    const navigationSlug = (query.currentPath as string).split('/')[2];

    const newLandingData = await apolloClient.query({
      query: gql`
        query {
          renderNavigation(
            navigationIdOrSlug: "${navigationSlug}"
            path: "${currentPath}"
            locale: "${currentLocale}"
            type: TREE
          ) {
            path
            related {
              attributes {
                __typename
                ... on LandingSite {
                  localizations {
                    data {
                      id
                      attributes {
                        locale
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `,
    });

    const newId = Number.parseInt(
      newLandingData?.data?.renderNavigation[0]?.related?.attributes?.localizations?.data.find(
        (item) => item.attributes.locale === locale,
      ).id,
    );

    const localizedLandingData = await apolloClient.query({
      query: gql`
        query {
          renderNavigation(navigationIdOrSlug:  "${navigationSlug}", locale: "${locale}", type: FLAT) {
            path
            related {
              id
            }
          }
        }
      `,
    });

    const newPath = localizedLandingData?.data?.renderNavigation?.find((item) => item?.related?.id === newId)?.path;

    return {
      redirect: {
        destination:
          locale === 'de-DE' ? `/c/${navigationSlug}/${newPath}` : `/${locale}/c/${navigationSlug}/${newPath}`,
        statusCode: 302,
      },
      // notFound: true,
    };
  }

  if (data) {
    if (data instanceof ResponseError && data.getStatus() == 404) {
      return {
        notFound: true,
      };
    } else if (typeof data === 'object' && 'target' in data) {
      return {
        redirect: {
          destination: data.target,
          statusCode: data.statusCode,
        } as Redirect,
      };
    }
  }

  if (data instanceof Error) {
    // @TODO: Render nicer error page in debug mode, which shows the error to
    // the developer and also outlines how to debug this (take a look at
    // frontastic-CLI).
    return {
      notFound: true,
    };
  }

  if (typeof data === 'string') {
    return {
      props: {
        data: { error: data },
        error: data,
      },
    };
  }

  if ((data as any)!.message === 'Could not resolve page from path') {
    return {
      notFound: true,
    };
  }

  if (params.path.length === 1) {
    return {
      redirect: {
        destination: '/',
        statusCode: 302,
      },
    };
  }

  const pagePath = `/${(params.path as string[]).join('/')}`;
  const rootPath = params.path[0];

  const allLandingsMainMenu = await apolloClient.query({
    query: gql`
      query RenderNaviagtion {
        renderNavigation(
          navigationIdOrSlug: "${rootPath}"
          locale: "${locale}"
          type: TREE
          path: "${pagePath}"
        ) {
            related {
            id
          }
        }
      }
    `,
  });

  const pageId = allLandingsMainMenu.data.renderNavigation[0].related.id;

  const landingData = await apolloClient.query({
    query: gql`
      query LandingSite {
        landingSite(id: "${pageId}") {
          data {
            id
            attributes {
              __typename
              locale
              name
              body {
                __typename
                ... on ComponentBasicBlockS1Banner {
                id
                title
                description
                customerGroups
                image {
                data {
                  attributes {
                    url
                  }
                }
                }
                buttonText
                buttonUrl
                targetBlank
              }
              ... on ComponentBasicBlockS4ProductList {
                id
                Products {
                  product
                }
                category
                customerGroups
              }
              ... on ComponentBasicBlockS3SingleProduct {
                id
                product
                customerGroups
              }
              ... on ComponentSectionsS55Form {
                id
                formName
                submitText
                emailRecipients
                fields {
                  id
                  inputType
                  displayName
                  placeholder
                  required
                }
                customerGroups
              }
              ... on ComponentSectionsCz12TextBlock {
                id
                title
                subtitle
                text
                customerGroups
              }
              ... on ComponentSectionsCz10Accordion {
                id
                title
                accordionItem {
                  title
                  text
                }
                customerGroups
              }
              ... on ComponentSectionsCz15Article {
                id
                title
                subtitle
                text
                image {
                  data {
                    attributes {
                      url
                      width
                      height
                    }
                  }
                }
                imageMobile {
                  data {
                    attributes {
                      url
                      width
                      height
                    }
                  }
                }
                customerGroups
              }
              ... on ComponentSectionsCz11Cta {
                id
                ctaItem {
                  id
                  icon {
                    data {
                      attributes {
                        url
                      }
                    }
                  }
                  title
                  subtitle
                  text
                  buttonText
                  buttonUrl
                  iconAlt
                }
                customerGroups
              }
              ... on ComponentSectionsCz13ImageWithText {
                id
                imageTextItem {
                alignment
                title
                subtitle
                text
                image {
                  data {
                    attributes {
                      url
                      width
                      height
                    }
                  }
                }
                imageMobile {
                  data {
                    attributes {
                      url
                      width
                      height
                    }
                  }
                }
                imageAlt
                buttonText
                buttonUrl
                buttonTargetBlank
                }
                customerGroups
              } 
              ... on ComponentSectionsCzAboutUs {
                title
                subtitle
                text
                AboutItem {
                  title
                  subtitle
                  image {
                    data {
                      attributes {
                        url
                        width
                        height
                      }
                    }
                  }
                  imageMobile {
                    data {
                      attributes {
                        url
                        width
                        height
                      }
                    }
                  }
                  imageAlt
                  imageTitle
                  linkUrl
                  targetBlank
                }
                customerGroups
              }
              ... on ComponentSectionsCz16Features {
                id
                title
                FeatItem {
                  id
                  icon {
                    data {
                      attributes {
                        url
                      }
                    }
                  }
                  imageAlt
                  imageTitle
                  title
                  text
                  linkUrl
                  targetBlank
                }
                customerGroups
              }
              ... on ComponentSectionsCz14ImageGallery {
                title
                galleryItem {
                  id
                  text
                  image {
                    data {
                      attributes {
                        url
                        width
                        height
                      }
                    }
                  }
                  imageMobile {
                    data {
                      attributes {
                        url
                        width
                        height
                      }
                    }
                  }
                  imageAlt
                  imageTitle
                  linkUrl
                  linkTargetBlank
                }
                customerGroups
              }
              }
            }
          }
        }
      }
    `,
  });

  return {
    props: {
      data: data || null,
      path: params.path,
      content: landingData.data.landingSite.data,
      locale: locale,
      ...(await serverSideTranslations(
        locale,
        ['common', 'cart', 'product', 'checkout', 'account', 'error', 'success', 'wishlist', 'newsletter'],
        nextI18nextConfig,
      )),
    },
  };
};
